import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { orderService, organizationService } from '../../services'
import DashboardSearchableSelect from './DashboardSearchableSelect'
import ReportTablePagination from '../SalesReport/ReportTable/ReportTablePagination'
import TableEmptyState from '../shared/TableEmptyState'
import TableLoadingRow from '../shared/TableLoadingRow'
import { DASHBOARD_COLORS, dashboardCardSx } from './dashboardFormatters'

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50]
const SEARCH_DEBOUNCE_MS = 400

const formatDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

const hoursSince = (dispatchedAt, now) => {
  const date = new Date(dispatchedAt)
  if (Number.isNaN(date.getTime())) return '—'
  const hours = (now - date.getTime()) / 36e5
  return `${hours.toFixed(1)}h`
}

const statusChip = (status) => {
  if (status === 'GROCER_SAYS_NOT_ARRIVED') {
    return (
      <Chip
        size="small"
        label="Grocer says not arrived"
        sx={{
          bgcolor: '#fff6e5',
          color: DASHBOARD_COLORS.amber,
          fontWeight: 700,
          fontSize: 11,
        }}
      />
    )
  }
  return (
    <Chip
      size="small"
      label="No response from grocer"
      sx={{
        bgcolor: '#fff1f3',
        color: DASHBOARD_COLORS.red,
        fontWeight: 700,
        fontSize: 11,
      }}
    />
  )
}

export default function UnreceivedDispatchAlertsSection() {
  const navigate = useNavigate()
  const [now, setNow] = useState(() => Date.now())
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [orgIds, setOrgIds] = useState('')
  const [partners, setPartners] = useState([])
  const [partnerLoading, setPartnerLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [items, setItems] = useState([])
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  })

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadPartners = useCallback(async (q = '') => {
    setPartnerLoading(true)
    try {
      const res = await organizationService.getOrganizationsPage({
        page: 1,
        limit: 50,
        search: q || undefined,
      })
      const rows = res?.items || res?.data || []
      setPartners(
        rows.map((item) => ({
          id: String(item.id),
          label: item.organization_name || item.name || `Shop ${item.id}`,
        })),
      )
    } catch {
      setPartners([])
    } finally {
      setPartnerLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadPartners('')
  }, [loadPartners])

  const fetchAlerts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await orderService.getUnreceivedDispatchAlerts({
        page,
        limit,
        search: search || undefined,
        orgIds: orgIds || undefined,
      })
      setItems(res?.items || [])
      setPagination(
        res?.pagination || {
          page,
          limit,
          total: 0,
          totalPages: 1,
        },
      )
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load dispatch alerts')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [limit, orgIds, page, search])

  useEffect(() => {
    void fetchAlerts()
  }, [fetchAlerts])

  const title = useMemo(() => {
    const total = pagination.total || 0
    return total > 0
      ? `Unreceived dispatches (${total})`
      : 'Unreceived dispatches'
  }, [pagination.total])

  return (
    <Paper sx={{ ...dashboardCardSx, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color: DASHBOARD_COLORS.red, display: 'flex' }}>
            <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Dispatched more than 2 hours ago and still unconfirmed
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <TextField
            size="small"
            placeholder="Search shop, shop ID, or order…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              minWidth: { sm: 300 },
              flex: { sm: '1 1 300px' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <DashboardSearchableSelect
            label="Partner Shop"
            options={partners}
            value={orgIds}
            onChange={(next) => {
              const ids = Array.isArray(next)
                ? next.map((option) => option.id).filter(Boolean).join(',')
                : String(next?.id || next || '')
              setOrgIds(ids)
              setPage(1)
            }}
            loading={partnerLoading}
            onSearch={loadPartners}
            multiple
            placeholder="All shops"
          />
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 960 }}>
          <TableHead>
            <TableRow>
              <TableCell>Shop name</TableCell>
              <TableCell>Shop Id</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Dispatch time</TableCell>
              <TableCell>Hours since dispatch</TableCell>
              <TableCell align="right">Reminders</TableCell>
              <TableCell align="right">Not Yet taps</TableCell>
              <TableCell>Last sync</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableLoadingRow colSpan={9} />
            ) : null}
            {items.map((row) => (
              <TableRow
                key={row.order_id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/orders/${row.order_id}`)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {row.organization_name}
                  </Typography>
                </TableCell>
                <TableCell>{row.org_id}</TableCell>
                <TableCell>#{row.order_id}</TableCell>
                <TableCell>{formatDateTime(row.dispatched_at)}</TableCell>
                <TableCell>{hoursSince(row.dispatched_at, now)}</TableCell>
                <TableCell align="right">{row.reminders_sent ?? 0}</TableCell>
                <TableCell align="right">{row.not_yet_taps ?? 0}</TableCell>
                <TableCell>{formatDateTime(row.last_sync_at)}</TableCell>
                <TableCell>{statusChip(row.alert_status)}</TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 ? (
              <TableEmptyState
                colSpan={9}
                message="No dispatched orders are overdue for receipt."
              />
            ) : null}
          </TableBody>
        </Table>
      </Box>

      <ReportTablePagination
        compact
        pagination={pagination}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setLimit(next)
          setPage(1)
        }}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
      />
    </Paper>
  )
}
